import { useCallback, useEffect, useState } from 'react';

import {
  listCompliance, approveCompliance, COMPLIANCE_ITEMS, ITEM_LABELS,
  type ComplianceEvent, type ComplianceItem, type ComplianceStatus,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort, formatDateTime, shortId } from '../../lib/format';
import './AdminPages.css';

/**
 * Compliance review queue.
 *
 * The operational point of this screen: an event cannot be published until all
 * eight checklist items are approved or marked not-required — the engine gates
 * publishEvent on overall_compliant in SQL, so this is the only place that
 * block gets lifted.
 */

const STATUS_ORDER: ComplianceStatus[] = ['pending', 'submitted', 'approved', 'not_required'];

const STATUS_LABEL: Record<ComplianceStatus, string> = {
  pending: 'Not submitted',
  submitted: 'Awaiting review',
  approved: 'Approved',
  not_required: 'Not required',
};

function ItemRow({
  eventId, item, status, docUrl, busy, onSet,
}: {
  eventId: string;
  item: ComplianceItem;
  status: ComplianceStatus;
  docUrl: string | null;
  busy: boolean;
  onSet: (item: ComplianceItem, status: ComplianceStatus) => void;
}) {
  return (
    <tr className={`adm-item adm-item--${status}`}>
      <td className="adm-item__name">{ITEM_LABELS[item]}</td>
      <td>
        <span className={`adm-pill adm-pill--${status}`}>{STATUS_LABEL[status]}</span>
      </td>
      <td className="adm-item__doc">
        {docUrl ? (
          // Organizer-supplied URL: open in a new context and withhold the referrer.
          <a href={docUrl} target="_blank" rel="noreferrer noopener">
            View document
          </a>
        ) : (
          <span className="adm-muted">No document</span>
        )}
      </td>
      <td className="adm-item__actions">
        <select
          className="adm-select"
          value={status}
          disabled={busy}
          aria-label={`${ITEM_LABELS[item]} status`}
          onChange={(e) => onSet(item, e.target.value as ComplianceStatus)}
          data-event={eventId}
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}

function EventPanel({
  event, onChanged,
}: {
  event: ComplianceEvent;
  onChanged: (next: ComplianceEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const apply = useCallback(
    async (items: Partial<Record<ComplianceItem, { status: ComplianceStatus }>>) => {
      setBusy(true);
      setError(null);
      try {
        const result = await approveCompliance(event.eventId, items);

        // Recompute the local row from what the engine actually returned rather than
        // assuming the change took.
        const updated: ComplianceEvent = {
          ...event,
          overallCompliant: result.overallCompliant,
          blockedFromPublishing: !result.overallCompliant,
          items: event.items.map((i) =>
            items[i.item] ? { ...i, status: items[i.item]!.status } : i,
          ),
        };
        updated.outstanding = updated.items.filter(
          (i) => i.status === 'pending' || i.status === 'submitted',
        ).length;
        updated.awaitingReview = updated.items.filter((i) => i.status === 'submitted').length;

        onChanged(updated);
        setFlash(
          result.overallCompliant
            ? 'All items cleared, this event can now be published.'
            : 'Saved.',
        );
        window.setTimeout(() => setFlash(null), 4000);
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err.message : 'Could not save that change.');
      } finally {
        setBusy(false);
      }
    },
    [event, onChanged],
  );

  const approveAll = () =>
    apply(
      Object.fromEntries(
        event.items
          .filter((i) => i.status !== 'approved' && i.status !== 'not_required')
          .map((i) => [i.item, { status: 'approved' as ComplianceStatus }]),
      ),
    );

  return (
    <article className={`adm-card${event.blockedFromPublishing ? ' adm-card--blocked' : ''}`}>
      <header className="adm-card__head">
        <div className="adm-card__title-wrap">
          <button
            type="button"
            className="adm-disclosure"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <span className="adm-disclosure__caret" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
            <span className="adm-card__title">{event.title}</span>
          </button>
          <p className="adm-card__meta">
            <span className={`adm-pill adm-pill--${event.eventStatus}`}>{event.eventStatus}</span>
            {' · '}
            {formatDateShort(event.eventDate)}
            {event.venue ? ` · ${event.venue}` : ''}
            {' · '}
            <span className="mono" title={event.eventId}>
              {shortId(event.eventId)}
            </span>
          </p>
          <p className="adm-card__meta adm-muted">
            Organizer: {event.organizer.name || '-'}{' '}
            <span className="mono">{event.organizer.email}</span>
          </p>
        </div>

        <div className="adm-card__status">
          {event.blockedFromPublishing ? (
            <span className="adm-flag adm-flag--blocked">
              Blocked from publishing
              <span className="adm-flag__detail">
                <span className="mono">{event.outstanding}</span> of 8 outstanding
              </span>
            </span>
          ) : (
            <span className="adm-flag adm-flag--clear">
              Cleared to publish
              <span className="adm-flag__detail">All 8 items resolved</span>
            </span>
          )}
          {event.awaitingReview > 0 && (
            <span className="adm-flag adm-flag--review">
              <span className="mono">{event.awaitingReview}</span> awaiting review
            </span>
          )}
        </div>
      </header>

      {open && (
        <div className="adm-card__body">
          {error && (
            <p className="adm-error" role="alert">
              {error}
            </p>
          )}
          {flash && (
            <p className="adm-flash" role="status">
              {flash}
            </p>
          )}

          <table className="adm-table adm-table--items">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Status</th>
                <th scope="col">Documentation</th>
                <th scope="col">Set</th>
              </tr>
            </thead>
            <tbody>
              {COMPLIANCE_ITEMS.map((item) => {
                const row = event.items.find((i) => i.item === item);
                return (
                  <ItemRow
                    key={item}
                    eventId={event.eventId}
                    item={item}
                    status={row?.status ?? 'pending'}
                    docUrl={row?.docUrl ?? null}
                    busy={busy}
                    onSet={(i, s) => apply({ [i]: { status: s } })}
                  />
                );
              })}
            </tbody>
          </table>

          <div className="adm-card__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy || event.outstanding === 0}
              onClick={approveAll}
            >
              {busy ? 'Saving…' : `Approve all ${event.outstanding} outstanding`}
            </button>
            <span className="adm-muted adm-hint">
              Approving every item flips overall_compliant, which is what lifts the
              database-level publish block.
            </span>
          </div>

          {event.checklistUpdatedAt && (
            <p className="adm-muted adm-hint">
              Checklist last updated {formatDateTime(event.checklistUpdatedAt)}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

export function AdminCompliance() {
  const [filter, setFilter] = useState<'blocked' | 'ready' | 'all'>('blocked');
  const [events, setEvents] = useState<ComplianceEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listCompliance(filter)
      .then((result) => {
        if (!cancelled) setEvents(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load the queue.');
        setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter]);

  const onChanged = useCallback((next: ComplianceEvent) => {
    setEvents((current) =>
      current ? current.map((e) => (e.eventId === next.eventId ? next : e)) : current,
    );
  }, []);

  const blocked = events?.filter((e) => e.blockedFromPublishing).length ?? 0;

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Compliance review</h1>
          <p className="adm-lede">
            Eight items per event. An event cannot be published until every item is
            approved or marked not required.
          </p>
        </div>
        {events && (
          <div className="adm-stat">
            <span className="adm-stat__value mono">{blocked}</span>
            <span className="adm-stat__label">blocked from publishing</span>
          </div>
        )}
      </header>

      <div className="adm-filters">
        {(['blocked', 'ready', 'all'] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={`adm-tab${filter === f ? ' is-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'blocked' ? 'Blocked' : f === 'ready' ? 'Cleared' : 'All events'}
          </button>
        ))}
      </div>

      {loading && <p className="adm-status">Loading…</p>}
      {error && (
        <p className="adm-status adm-error" role="alert">
          {error}
        </p>
      )}
      {!loading && events && events.length === 0 && (
        <p className="adm-status adm-muted">
          {filter === 'blocked'
            ? 'Nothing is blocked. Every event has cleared compliance.'
            : 'No events match.'}
        </p>
      )}

      <div className="adm-list">
        {events?.map((event) => (
          <EventPanel key={event.eventId} event={event} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}
